import { useCallback, useContext, useMemo } from 'react'
import { View, Text, ScrollView, Pressable, Platform, useWindowDimensions } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { ArrowLeft, Crown } from 'lucide-react-native'
import { ContactForm } from '../../../../components/landing/ContactForm'
import { MarketingPageFooter } from '../../../../components/landing/MarketingPageFooter/MarketingPageFooter'
import { LanguageContext } from '../../../i18n'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { usePublicMarketingWebScrollLayout } from '../../../shared/hooks/usePublicMarketingWebScrollLayout'
import { LANDING_MARKETING_HOME_PRICING_HREF } from '../../../landing/constants/landingSiteNav.constants'
import { LANDING_PHONE_MAX_WIDTH } from '../../../landing/constants/landingMarketingBreakpoints.constants'
import { PRICING_PLAN_TIERS } from '../../constants/pricingPlans.constants'
import { usePricingI18n } from '../../hooks/usePricingI18n'
import { getEnterpriseContactScreenStyles } from './EnterpriseContactScreen.styles'

const CTA_STACKED_WIDTH = 940

export function EnterpriseContactScreen() {
  const theme = useTheme()
  const router = useRouter()
  const { width } = useWindowDimensions()
  const styles = useMemo(() => getEnterpriseContactScreenStyles(theme), [theme])
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('EnterpriseContactScreen must be used within LanguageProvider')
  }
  const { t } = languageContext

  usePublicMarketingWebScrollLayout()

  const { planCards, copy } = usePricingI18n()

  const enterprisePlan = useMemo(
    () => planCards.find((c) => c.tier === PRICING_PLAN_TIERS.ENTERPRISE),
    [planCards],
  )

  const isStacked = width > 0 && width < CTA_STACKED_WIDTH
  const isPhone = width > 0 && width < LANDING_PHONE_MAX_WIDTH

  const handleBackToPlans = useCallback(() => {
    router.push(LANDING_MARKETING_HOME_PRICING_HREF as Href)
  }, [router])

  const backButton = (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={copy.goBack}
      onPress={handleBackToPlans}
      style={({ pressed, hovered }) => [
        styles.backButton,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.backButtonPressed,
        hovered && Platform.OS === 'web' && styles.backButtonHoverWeb,
      ]}
    >
      <ArrowLeft size={22} color={theme.primary} strokeWidth={2.5} />
      <Text style={styles.backLabel}>{copy.goBack}</Text>
    </Pressable>
  )

  const cardContent = (
    <View style={[styles.card, isStacked && styles.cardStacked, isPhone && styles.cardPhone]}>
      <View style={[styles.copy, isStacked && styles.copyStacked, isPhone && styles.copyPhone]}>
        <View style={styles.badge}>
          <Crown size={14} color="#6B46C1" />
          <Text style={styles.badgeText}>
            {enterprisePlan?.name ?? t.pricing.plans.enterprise.name}
          </Text>
        </View>
        <Text style={[styles.title, isPhone && styles.titlePhone]}>
          {t.contact.title}
        </Text>
        <Text style={[styles.tagline, isStacked && styles.taglineStacked, isPhone && styles.taglinePhone]}>
          {enterprisePlan?.tagline ?? t.pricing.plans.enterprise.tagline}
        </Text>
      </View>
      <View style={[styles.formWrap, isPhone && styles.formWrapPhone]}>
        <ContactForm
          translations={{
            nameLabel: t.contact.form.nameLabel,
            emailLabel: t.contact.form.emailLabel,
            messageLabel: t.contact.form.messageLabel,
            submitButton: t.contact.form.submitButton,
            submitting: t.contact.form.submitting,
            successMessage: t.contact.form.successMessage,
            errorMessage: t.contact.form.errorMessage,
            namePlaceholder: t.contact.form.namePlaceholder,
            emailPlaceholder: t.contact.form.emailPlaceholder,
            messagePlaceholder: t.contact.form.messagePlaceholder,
            validation: t.contact.form.validation,
          }}
          testID="enterprise-contact-form"
        />
      </View>
    </View>
  )

  if (Platform.OS === 'web') {
    return (
      <View style={styles.webPageRoot}>
        <View style={styles.topBar}>{backButton}</View>
        <View style={[styles.section, isPhone && styles.sectionPhone]}>
          {cardContent}
        </View>
        <MarketingPageFooter contactMode="route" />
      </View>
    )
  }

  return (
    <View style={styles.root}>
      <View style={styles.topBar}>{backButton}</View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {cardContent}
        <MarketingPageFooter contactMode="route" />
      </ScrollView>
    </View>
  )
}
